/**
 * The public site's frame and building blocks: the floating nav, sections, buttons, the closing call to action and
 * the footer with the wordmark. Design notes in docs/public-site-design.md.
 */
import '@fontsource-variable/mona-sans/wdth.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useApi } from '../lib/api';
import type { Me } from '../lib/types';
import { Icons } from '../components/icons';
import { cx } from '../components/ui';
import { Art } from './art';
import type { Faq } from './content';
import { sessionHint, usePublicPlan, useSite, useTitle } from './hooks';
import { openPixelChoices, PixelChoices, usePixelPageVisit } from './pixel';

const NAV = [
  { label: 'Product', to: '/#product' },
  { label: 'Assistant', to: '/#assistant' },
  { label: 'Developers', to: '/#developers' },
  { label: 'Pricing', to: '/pricing' },
];

const FOOTER = [
  { title: 'Product', links: [{ label: 'Overview', to: '/' }, { label: 'The ledger', to: '/#product' }, ...NAV.slice(1)] },
  {
    title: 'Compare',
    links: [
      { label: 'vs Rillet', to: '/compare/raremargin-vs-rillet' },
      { label: 'vs QuickBooks Online', to: '/compare/raremargin-vs-quickbooks-online' },
      { label: 'vs NetSuite', to: '/compare/raremargin-vs-netsuite' },
    ],
  },
  {
    title: 'Guides',
    links: [
      { label: 'ASC 606 revenue', to: '/docs/asc-606-revenue-recognition' },
      { label: 'Continuous close', to: '/docs/how-continuous-close-works' },
      { label: 'AI and approvals', to: '/docs/ai-journal-entries-and-approvals' },
      { label: 'API, MCP and webhooks', to: '/docs/api-mcp-server-and-webhooks' },
      { label: 'Claude, ChatGPT and Cursor', to: '/docs/use-raremargin-in-claude-chatgpt-and-cursor' },
      { label: 'Setup and migration', to: '/docs/set-up-raremargin-and-move-your-books' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Contact', to: '/contact' },
      { label: 'Sign in', to: '/login' },
      { label: 'Start free trial', to: '/signup' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of service', to: '/terms' },
      { label: 'Privacy policy', to: '/privacy' },
      { label: 'Refund policy', to: '/refunds' },
      { label: 'Data processing', to: '/dpa' },
    ],
  },
];

/** Header, page, footer. Scrolls to the top on a new page, or to `#section` when the link has one. */
export function SiteLayout({ title, children }: { title: string; children: ReactNode }) {
  useTitle(title);
  usePixelPageVisit();
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const target = hash && document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div className="min-h-screen bg-site-paper font-display text-[16px] leading-normal text-site-ink antialiased [font-stretch:100%] selection:bg-site-mark selection:text-site-ink">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-site-white focus:px-3 focus:py-2">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">{children}</main>
      <SiteFooter />
      <PixelChoices />
      <div aria-hidden className="site-grain" />
    </div>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Shares the app's session query, asked only when this browser was signed in last time, so visitors make no call.
  const me = useApi<Me>(sessionHint.get() ? '/api/auth/me' : null, { retry: false, staleTime: 60_000 });
  const signedIn = !!me.data;
  useEffect(() => setOpen(false), [location]);

  const link = 'rounded-[12px] px-3 py-2 text-[14.5px] font-[480] text-site-ink-2 transition-colors hover:bg-site-ink/[0.05] hover:text-site-ink';
  return (
    <header className="sticky top-0 z-40 px-3 pt-3 sm:px-4">
      <nav aria-label="Main" className="mx-auto flex h-[58px] max-w-[1232px] items-center gap-2 rounded-[22px] border border-white/60 bg-site-white/[0.72] pr-2 pl-4 shadow-[0_12px_32px_-20px_rgb(27_26_24/0.45),0_0_0_1px_rgb(27_26_24/0.05)] backdrop-blur-2xl backdrop-saturate-150">
        <Wordmark />
        <div className="mx-auto hidden items-center gap-0.5 md:flex">
          {NAV.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => cx(link, isActive && !l.to.includes('#') && 'text-site-ink')}>
              {l.label}
            </NavLink>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1.5 md:ml-0">
          {signedIn ? (
            <SiteButton to="/" variant="ink" size="sm" className="!rounded-[14px]">
              Open RareMargin
            </SiteButton>
          ) : (
            <>
              <Link to="/login" className={cx(link, 'hidden sm:block')}>
                Sign in
              </Link>
              <SiteButton to="/signup" variant="ink" size="sm" className="!rounded-[14px]">
                Start free trial
              </SiteButton>
            </>
          )}
          <button
            type="button"
            className="grid size-9 place-items-center rounded-[12px] text-site-ink-2 hover:bg-site-ink/[0.05] md:hidden"
            aria-expanded={open}
            aria-controls="site-menu"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((o) => !o)}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
              {open ? <path d="M4 4l10 10M14 4L4 14" /> : <path d="M2.5 5.5h13M2.5 12.5h13" />}
            </svg>
          </button>
        </div>
      </nav>
      {open && (
        <div id="site-menu" className="mx-auto mt-2 grid max-w-[1232px] gap-0.5 rounded-[22px] border border-white/60 bg-site-white/[0.86] p-2 shadow-[0_18px_40px_-20px_rgb(27_26_24/0.4),0_0_0_1px_rgb(27_26_24/0.05)] backdrop-blur-2xl backdrop-saturate-150 md:hidden">
          {NAV.map((l) => (
            <Link key={l.to} to={l.to} className={link}>
              {l.label}
            </Link>
          ))}
          {!signedIn && (
            <Link to="/login" className={link}>
              Sign in
            </Link>
          )}
        </div>
      )}
    </header>
  );
}

export function Wordmark({ light }: { light?: boolean }) {
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2 rounded-lg" aria-label="RareMargin home">
      <Icons.Logo size={26} />
      <span className={cx('text-[18px] font-[640] tracking-[-0.04em]', light ? 'text-site-white' : 'text-site-ink')}>RareMargin</span>
    </Link>
  );
}

type Variant = 'ink' | 'green' | 'white' | 'lime' | 'oat' | 'accent' | 'on-dark' | 'outline';
const VARIANT: Record<Variant, string> = {
  ink: 'bg-site-ink text-white hover:bg-black',
  green: 'bg-site-green text-site-white hover:bg-site-green-2',
  white: 'bg-site-white text-site-ink shadow-[0_1px_2px_rgb(27_26_24/0.08)] hover:bg-site-oat',
  lime: 'bg-site-lime text-site-lime-ink hover:bg-site-lime-2',
  oat: 'bg-site-oat text-site-ink hover:bg-site-oat-2',
  /** A pillar's colour: set `--accent` (and `--accent-text` when white text isn't right) on the button or a parent. */
  accent: 'bg-[color:var(--accent)] text-[color:var(--accent-text,white)] hover:brightness-110',
  'on-dark': 'border border-white/30 text-site-white hover:border-white/70 hover:bg-white/5',
  outline: 'border border-site-rule-strong bg-site-white text-site-ink hover:border-site-ink/40',
};
const SIZE = { sm: 'h-9 px-3.5 text-[14px]', md: 'h-11 px-5 text-[15px]', lg: 'h-12 px-6 text-[15.5px]' };

/** A link styled as a button. `to` stays in the app; `href` leaves it (mailto, Dodo). */
export function SiteButton({ to, href, variant = 'ink', size = 'md', arrow, children, className, style }: { to?: string; href?: string; variant?: Variant; size?: keyof typeof SIZE; arrow?: boolean; children: ReactNode; className?: string; style?: CSSProperties }) {
  const cls = cx('group inline-flex shrink-0 items-center justify-center gap-2 rounded-[12px] font-[540] whitespace-nowrap transition-[background-color,border-color,filter]', SIZE[size], VARIANT[variant], className);
  const body = (
    <>
      {children}
      {arrow && (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden>
          <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
      )}
    </>
  );
  return href ? (
    <a href={href} className={cls} style={style}>
      {body}
    </a>
  ) : (
    <Link to={to!} className={cls} style={style}>
      {body}
    </Link>
  );
}

const TONE = { paper: 'bg-site-paper', oat: 'bg-site-oat', white: 'bg-site-white', green: 'bg-site-green text-site-white', mark: 'bg-site-mark' };

/**
 * A full-width band. Pass `id` to make it a nav target; the offset keeps the heading clear of the sticky nav. `stage`
 * starts the page on oat that fades into white, running up under the nav; `stage="soft"` is a shorter fade, for long
 * text. `rules` draws column rules either side of the content.
 */
export function Band({ id, tone = 'paper', rules = false, stage, className, inner, children }: { id?: string; tone?: keyof typeof TONE; rules?: boolean; stage?: boolean | 'soft'; className?: string; inner?: string; children: ReactNode }) {
  return (
    <section id={id} className={cx('relative scroll-mt-20 overflow-x-clip', TONE[tone], stage && '-mt-[70px] pt-[70px]', className)}>
      {stage && <div aria-hidden className={cx('pointer-events-none absolute inset-x-0 top-0', stage === 'soft' ? 'site-stage-soft h-[min(420px,100%)]' : 'site-stage h-[min(640px,100%)]')} />}
      <div className="relative mx-auto max-w-[1264px] px-5 sm:px-8">
        <div className={cx(rules && 'lg:border-x', tone === 'green' ? 'border-white/10' : 'border-site-rule', 'py-[72px] lg:px-10 lg:py-[112px]', inner)}>{children}</div>
      </div>
    </section>
  );
}

/** A rounded coloured panel inset from the window's edges, the way Clay frames its big sections. */
export function Panel({ id, className, outer, children }: { id?: string; className?: string; outer?: string; children: ReactNode }) {
  return (
    <section id={id} className={cx('scroll-mt-20 px-2 sm:px-3', outer)}>
      <div className={cx('relative mx-auto max-w-[1600px] overflow-hidden rounded-[28px] sm:rounded-[40px]', className)}>{children}</div>
    </section>
  );
}

/** `01 / Revenue`: a ledger-style reference above a heading. */
export function Eyebrow({ n, children, className }: { n?: string; children: ReactNode; className?: string }) {
  return (
    <div className={cx('site-eyebrow flex items-center gap-2', className ?? 'text-site-muted')}>
      {n && (
        <>
          <span className="opacity-100">{n}</span>
          <span className="opacity-50">/</span>
        </>
      )}
      <span>{children}</span>
    </div>
  );
}

export const Mark = ({ children }: { children: ReactNode }) => <span className="site-mark">{children}</span>;

/**
 * Questions that open to their answers. The answers are in the page's HTML even while closed, and the same text is the
 * page's FAQPage data (content.ts), so search engines and AI assistants can quote them.
 */
export function FaqList({ items }: { items: Faq[] }) {
  return (
    <div className="border-t border-site-rule">
      {items.map((x) => (
        <details key={x.q} className="group border-b border-site-rule">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 [&::-webkit-details-marker]:hidden">
            <h3 className="text-[17px] leading-snug font-[600]">{x.q}</h3>
            <span className="grid size-7 shrink-0 place-items-center rounded-full border border-site-rule-strong text-site-ink-2 transition-transform group-open:rotate-45" aria-hidden>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M6 1.5v9M1.5 6h9" />
              </svg>
            </span>
          </summary>
          <p className="max-w-[62ch] pb-6 text-[15.5px] leading-relaxed text-site-ink-2">
            <Linked text={x.a} links={x.links} />
          </p>
        </details>
      ))}
    </div>
  );
}

/** `text` with each link's text (in order) made a link: inside the site, or out to a source in a new tab. */
function Linked({ text, links = [] }: Pick<Faq, 'links'> & { text: string }) {
  const cls = 'text-site-green underline-offset-4 hover:underline';
  const parts: ReactNode[] = [];
  let rest = text;
  for (const l of links) {
    const at = rest.indexOf(l.text);
    if (at < 0) continue;
    parts.push(
      rest.slice(0, at),
      l.to.startsWith('/') ? (
        <Link key={l.to} to={l.to} className={cls}>
          {l.text}
        </Link>
      ) : (
        <a key={l.to} href={l.to} target="_blank" rel="noreferrer" className={cls}>
          {l.text}
        </a>
      ),
    );
    rest = rest.slice(at + l.text.length);
  }
  return [...parts, rest];
}

/** The closing panel: the three desk objects from the pillars on the green stage, the way in written above them. */
export function ClosingCta() {
  const trialDays = usePublicPlan().data?.trialDays ?? 14;
  return (
    <Panel className="bg-site-green text-center text-site-white" outer="pb-2 sm:pb-3">
      <div className="relative z-[2] mx-auto max-w-[920px] px-6 pt-20 sm:pt-28">
        <Eyebrow className="justify-center text-site-lime">Continuous close</Eyebrow>
        <h2 className="site-h2 mt-6">
          Stop closing the books. <span className="text-site-lime">Keep them closed.</span>
        </h2>
        <p className="site-lead mx-auto mt-6 max-w-[46ch] text-site-white/75">
          Start a {trialDays}-day trial on your own data, or on a sample SaaS company with 14 months of history. No card needed.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <SiteButton to="/signup" variant="lime" size="lg" arrow>
            Start free trial
          </SiteButton>
          <SiteButton to="/contact" variant="white" size="lg">
            Talk to us
          </SiteButton>
        </div>
      </div>
      <Art
        name="closing"
        sizes="(min-width: 1600px) 1600px, 100vw"
        className="relative z-[1] -mt-[6vw] min-[1600px]:-mt-[96px]"
        alt="Three desk objects on a green stage: a tray of blue month cubes, a console of toggle switches with a lit button, and a keyboard of keycaps under a monitor of code."
      />
    </Panel>
  );
}

function SiteFooter() {
  const site = useSite().data;
  return (
    <footer className="bg-site-paper">
      {/* The wordmark as a ledger total: set full width, double-ruled underneath. */}
      <div className="mx-auto max-w-[1264px] overflow-hidden px-5 pt-16 sm:px-8 lg:pt-24">
        <div aria-hidden className="border-b-[5px] border-double border-site-green pb-2 text-center font-display text-[clamp(48px,17vw,232px)] leading-[0.8] font-[620] tracking-[-0.065em] text-site-green select-none">
          RareMargin
        </div>
      </div>
      <div className="mx-auto grid max-w-[1264px] grid-cols-2 gap-10 px-5 py-14 sm:grid-cols-3 sm:px-8 lg:grid-cols-[1.4fr_repeat(5,1fr)] lg:gap-8">
        <div className="col-span-2 sm:col-span-3 lg:col-span-1">
          <Wordmark />
          <p className="mt-4 max-w-[34ch] text-[15px] leading-relaxed text-site-ink-2">The general ledger that closes as you go, for finance teams at SaaS companies.</p>
        </div>
        {FOOTER.map((col) => (
          <div key={col.title}>
            <div className="site-eyebrow text-site-muted">{col.title}</div>
            {/* Taller links on phones, so a thumb hits the right one. */}
            <ul className="mt-3 space-y-1 text-[15px] sm:mt-4 sm:space-y-2.5">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-block py-1 text-site-ink-2 transition-colors hover:text-site-ink sm:py-0">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-site-rule">
        <div className="mx-auto flex max-w-[1264px] flex-wrap items-center justify-between gap-x-6 gap-y-2 px-5 py-6 text-[13.5px] text-site-muted sm:px-8">
          <span>
            © {new Date().getFullYear()} {site?.businessName ?? 'RareMargin'}
          </span>
          {site && (
            <span className="flex flex-wrap gap-x-6 gap-y-2">
              {site.redditPixelId && (
                <button type="button" onClick={openPixelChoices} className="hover:text-site-ink">
                  Your privacy choices
                </button>
              )}
              <a href={`mailto:${site.supportEmail}`} className="hover:text-site-ink">
                {site.supportEmail}
              </a>
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
